/**
 * runBattle — launches BattleScene over a caller scene and resolves with the BattleOutcome (owner: combat-builder).
 *
 * Sequence: fade the UI out, load the `battle` manifest group through the Router (real atlases; dev
 * sheets only when it fails), launch Battle in parallel, pause the caller; on the bout's end resume the
 * caller and fade back in. While the bout runs `ctx.skipHandler` is cleared so Esc reaches the pause
 * menu (the caller's cinematic is paused and cannot be skipped anyway); it is restored afterwards.
 */
import type Phaser from 'phaser';
import type { BattleOutcome } from '@/core/types/contracts';
import { ctxOf, type GameContext } from '@/core/context';
import { SceneKeys } from '@/core/scenes/keys';
import { SeededRng } from '@/core/rng';
import { BattleScene } from './BattleScene';
import { BATTLES } from './registry';
import type { BattleLaunchData } from './types';

export interface RunBattleOptions {
  /** Override the seed (default: save seed mixed with the config id). */
  seed?: number;
  /** Showcase mode — the caller is a showcase, not a story scene. */
  standalone?: boolean;
}

/** Deterministic per-save seed for a battle id. */
export function battleSeed(baseSeed: number, configId: string): number {
  return (baseSeed ^ SeededRng.hash(`battle:${configId}`)) >>> 0;
}

/** Launches the battle scene over the caller and resolves with the outcome when it ends. */
export function runBattle(from: Phaser.Scene, configId: string, opts: RunBattleOptions = {}): Promise<BattleOutcome> {
  if (!BATTLES[configId]) { console.warn(`[combat] unknown battle "${configId}" — resolving as 'rounds'`); return Promise.resolve('rounds'); }
  const mgr = from.scene;
  if (!mgr.get(SceneKeys.BATTLE)) {
    const added = mgr.add(SceneKeys.BATTLE, BattleScene, false);
    if (!added || !mgr.get(SceneKeys.BATTLE)) { console.warn('[combat] could not register BattleScene — battle skipped'); return Promise.resolve('rounds'); }
  }
  if (mgr.isActive(SceneKeys.BATTLE) || mgr.isPaused(SceneKeys.BATTLE)) { console.warn('[combat] a battle is already running — skipped'); return Promise.resolve('rounds'); }
  let ctx: GameContext | null = null;
  try { ctx = ctxOf(from) ?? null; } catch { /* no context in tests */ }
  const seed = opts.seed ?? battleSeed(ctx?.seed ?? 1157, configId);
  const callerKey = from.scene.key;
  const savedSkip = ctx?.skipHandler ?? null;

  return new Promise<BattleOutcome>((resolve) => {
    let settled = false;
    const battle = mgr.get(SceneKeys.BATTLE) as Phaser.Scene;
    const uiFade = (out: boolean, ms: number): Promise<void> => {
      const ui = mgr.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
      return ui?.fade && ui.scene.isActive() ? ui.fade(out, ms).catch(() => undefined) : Promise.resolve();
    };
    let skipCleared = false;
    const settle = (outcome: BattleOutcome, callerAlive = true) => {
      if (settled) return;
      settled = true;
      battle.events.off('shutdown', onBattleShutdown);
      from.events.off('shutdown', onCallerShutdown);
      // Give Esc back to the cinematic only if it is still there and nobody re-registered a skip meanwhile.
      if (skipCleared && callerAlive && ctx && ctx.skipHandler === null) ctx.skipHandler = savedSkip;
      if (mgr.isPaused(callerKey)) mgr.resume(callerKey);
      void uiFade(false, 320);
      resolve(outcome);
    };
    const onBattleShutdown = () => settle('rounds');
    // The caller died under us (debug jump / router transition): stop the bout, resolve quietly.
    const onCallerShutdown = () => { if (mgr.isActive(SceneKeys.BATTLE) || mgr.isPaused(SceneKeys.BATTLE)) mgr.stop(SceneKeys.BATTLE); settle('rounds', false); };
    const data: BattleLaunchData = { configId, callerKey, seed, onDone: settle, ...(opts.standalone ? { standalone: true } : {}) };
    battle.events.once('shutdown', onBattleShutdown);
    from.events.once('shutdown', onCallerShutdown);
    // Real battle art: the manifest group is loaded through the Router's LoadingScene (30 s guard inside);
    // the Battle scene draws its dev sheets for any key still missing afterwards.
    const loadArt = ctx ? ctx.router.loadGroup('battle').catch((e: unknown) => console.warn('[combat] battle assets failed to load — using dev sheets', e)) : Promise.resolve();
    void Promise.all([uiFade(true, 300), loadArt]).then(() => {
      if (settled) return;
      if (!from.scene.isActive() && !from.scene.isPaused()) { settle('rounds', false); return; }
      if (ctx && ctx.skipHandler === savedSkip) { ctx.skipHandler = null; skipCleared = true; }
      mgr.launch(SceneKeys.BATTLE, data);
      mgr.bringToTop(SceneKeys.BATTLE);
      if (mgr.get(SceneKeys.UI)) mgr.bringToTop(SceneKeys.UI);
      mgr.pause(callerKey);
    });
  });
}

